import { downloadExternalAuditAttachment } from '../../../api/externalAudit';
import {
  EXTERNAL_AUDIT_FINDING_STATUSES,
  EXTERNAL_AUDIT_FINDING_TYPES,
  EXTERNAL_AUDIT_TYPES,
  ExternalAuditReport,
  parseAuditors,
  parseFindings,
  summarizeAuditors,
  summarizeFindings,
} from '../../../types/externalAudit';
import ApprovalStampBox from '../ApprovalStampBox';
import '../../quality/EducationAnnualPlanTab.css';
import '../../quality/QualityShell.css';
import '../../quality/ExternalAuditPanel.css';
import '../../product/InspectionFormSheet.css';
import '../../product/NonconformanceReportPanel.css';
import { PreviewAttachmentList } from './previewShared';

interface Props {
  detail: ExternalAuditReport;
}

function FindingSummaryChips({ findings }: { findings: ReturnType<typeof parseFindings> }) {
  const s = summarizeFindings(findings);
  if (s.total === 0) {
    return <span className="ea-chip ea-chip--muted">지적 없음</span>;
  }
  return (
    <div className="ea-chips">
      <span className="ea-chip">전체 {s.total}</span>
      {s.nc > 0 ? <span className="ea-chip ea-chip--nc">NC {s.nc}</span> : null}
      {s.ofi > 0 ? <span className="ea-chip ea-chip--ofi">OFI {s.ofi}</span> : null}
      {s.obs > 0 ? <span className="ea-chip ea-chip--obs">OBS {s.obs}</span> : null}
      {s.open > 0 ? <span className="ea-chip ea-chip--open">미결 {s.open}</span> : null}
    </div>
  );
}

function ExternalAuditPreviewView({ detail }: Props) {
  const auditors = parseAuditors(detail.auditorsJson);
  const findings = parseFindings(detail.findingsJson);

  return (
    <div className="quality-form">
      <section className="quality-form__section">
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 16,
            alignItems: 'flex-start',
            marginBottom: 12,
          }}
        >
          <h3 style={{ margin: 0 }}>외부심사</h3>
          <ApprovalStampBox
            authorName={detail.authorName}
            reviewerName={detail.reviewerName}
            approverName={detail.approverName}
          />
        </div>
        <div className="quality-form__grid">
          <label>
            문서번호
            <input readOnly value={detail.reportNo || '-'} />
          </label>
          <label>
            심사일
            <input type="date" readOnly value={detail.auditDate || ''} />
          </label>
          <label className="is-span-2">
            제목 *
            <input readOnly value={detail.title || ''} />
          </label>
          <label>
            심사유형
            <select value={detail.auditType || ''} disabled>
              {EXTERNAL_AUDIT_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            인증기관/고객사
            <input readOnly value={detail.auditOrg || ''} />
          </label>
          <label>
            장소
            <input readOnly value={detail.location || ''} />
          </label>
          <label>
            후속기한
            <input type="date" readOnly value={detail.followUpDue || ''} />
          </label>
          <label className="is-span-2">
            심사범위
            <textarea rows={3} readOnly value={detail.scopeText || ''} />
          </label>
          <label className="is-span-2">
            결론
            <textarea rows={4} readOnly value={detail.conclusion || ''} />
          </label>
          <label className="is-span-2">
            비고
            <textarea rows={3} readOnly value={detail.remark || ''} />
          </label>
        </div>
      </section>

      <section className="quality-form__section">
        <div className="ea-section-head">
          <div>
            <h3 style={{ margin: 0 }}>심사관</h3>
          </div>
          <div className="ea-section-head__right">
            <span className="ea-chip ea-chip--muted">{summarizeAuditors(auditors)}명</span>
          </div>
        </div>
        <div className="ea-auditor-list">
          {auditors.map((row, index) => (
            <div key={row.id} className="ea-auditor-card">
              <div className="ea-auditor-card__title">심사관 {index + 1}</div>
              <div className="ea-auditor-card__grid">
                <label>
                  성명
                  <input readOnly value={row.name} />
                </label>
                <label>
                  소속
                  <input readOnly value={row.org} />
                </label>
                <label>
                  역할
                  <input readOnly value={row.role} />
                </label>
                <label>
                  휴대폰
                  <input type="tel" readOnly value={row.phone} />
                </label>
                <label className="is-span-2">
                  이메일
                  <input type="email" readOnly value={row.email} />
                </label>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="quality-form__section">
        <div className="ea-section-head">
          <div>
            <h3 style={{ margin: 0 }}>지적 · 시정조치</h3>
          </div>
          <div className="ea-section-head__right">
            <FindingSummaryChips findings={findings} />
          </div>
        </div>
        <div className="quality-table-wrap">
          <table className="quality-table">
            <thead>
              <tr>
                <th style={{ width: 120 }}>유형</th>
                <th style={{ width: 100 }}>조항</th>
                <th>지적사항</th>
                <th>시정조치</th>
                <th style={{ width: 120 }}>기한</th>
                <th style={{ width: 88 }}>상태</th>
              </tr>
            </thead>
            <tbody>
              {findings.map((row) => (
                <tr key={row.id}>
                  <td>
                    <select value={row.findingType} disabled>
                      {EXTERNAL_AUDIT_FINDING_TYPES.map((t) => (
                        <option key={t.value} value={t.value}>
                          {t.label}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <textarea rows={2} readOnly value={row.clause} />
                  </td>
                  <td>
                    <textarea rows={2} readOnly value={row.finding} />
                  </td>
                  <td>
                    <textarea rows={2} readOnly value={row.correctiveAction} />
                  </td>
                  <td>
                    <input type="date" readOnly value={row.dueDate} />
                  </td>
                  <td>
                    <select value={row.status} disabled>
                      {EXTERNAL_AUDIT_FINDING_STATUSES.map((t) => (
                        <option key={t.value} value={t.value}>
                          {t.label}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="quality-form__section">
        <h3 style={{ margin: '0 0 8px' }}>첨부파일</h3>
        <p className="quality-form__hint">관련 파일을 첨부할 수 있습니다.</p>
        <PreviewAttachmentList
          files={detail.attachments}
          onDownload={(file) => {
            void downloadExternalAuditAttachment(
              detail.reportId,
              file.attachmentId,
              file.fileName
            );
          }}
        />
      </section>
    </div>
  );
}

export default ExternalAuditPreviewView;
